/** Watch a dispatched design-import ingest and hand over to the review page.
 *
 * The wizard closes as soon as the task starts (the operator lands on the
 * task's live logs), so — like `watchExportTask` — this is a detached polling
 * loop tied to no component lifecycle. When ingest completes, a long-lived
 * snackbar offers "Review proposal"; the ingest result carries the created
 * proposal id. The proposal also stays reachable under Design Imports →
 * Proposals whatever happens here.
 */
import { isActiveState, result as fetchResult, retrieve, TASK_STATE } from '@/api/bgtasks';
import type { SnackbarShowOptions } from '@/components/Snackbar';

interface SnackbarLike {
  show: (
    message: string,
    severity?: 'success' | 'error' | 'info' | 'warning',
    opts?: SnackbarShowOptions,
  ) => void;
}

/** SPA-navigate via the router. Lazy import AT CLICK TIME — this module is
 *  reachable from pages the route table imports (module-cycle guard, same as
 *  watchExportTask.openBackgroundTask). */
function navigateTo(path: string): void {
  void import('@/routes').then(({ router }) => router.navigate(path));
}

const POLL_MS = 4_000;
const MAX_WATCH_MS = 30 * 60 * 1_000;
const MAX_CONSECUTIVE_ERRORS = 5;
const OFFER_MS = 30_000;
/** The result lands in the task archive slightly after COMPLETED. */
const RESULT_RETRIES = 8;
const RESULT_RETRY_MS = 1_500;

export function watchIngestTask(taskId: string, snackbar: SnackbarLike): void {
  const startedAt = Date.now();
  let consecutiveErrors = 0;

  const viewTask = {
    label: 'View task',
    onClick: () => navigateTo(`/bgtasks/${taskId}?tab=logs`),
  };

  const offerProposal = async (attempt = 0): Promise<void> => {
    try {
      const { result } = await fetchResult(taskId);
      const proposalId = result?.proposal_id as number | undefined;
      const blocking = (result?.blocking_questions as number | undefined) ?? 0;
      const designs = (result?.designs as unknown[] | undefined)?.length ?? 1;
      if (!proposalId) {
        snackbar.show('Design import finished.', 'success', { action: viewTask });
        return;
      }
      const what = designs > 1 ? `${designs} designs are` : 'The design is';
      const gate = blocking > 0 ? ` ${blocking} blocking question(s) need answers before apply.` : '';
      // Straight to the review page — waiting for ingest IS the operator's
      // current task, and the snackbar explains the jump.
      navigateTo(`/notifications/design-imports/proposals/${proposalId}`);
      snackbar.show(`${what} ready for review.${gate}`, 'success', {
        autoHideDuration: OFFER_MS,
        action: viewTask,
      });
    } catch {
      if (attempt < RESULT_RETRIES) {
        window.setTimeout(() => void offerProposal(attempt + 1), RESULT_RETRY_MS);
      } else {
        snackbar.show('Design import finished — open the proposal from Design Imports → Proposals.', 'success', {
          action: viewTask,
        });
      }
    }
  };

  const poll = async (): Promise<void> => {
    if (Date.now() - startedAt > MAX_WATCH_MS) return;
    try {
      const task = await retrieve(taskId);
      consecutiveErrors = 0;
      if (isActiveState(task.state)) {
        window.setTimeout(poll, POLL_MS);
        return;
      }
      if (task.state === TASK_STATE.CANCELLED) {
        snackbar.show('Design import cancelled.', 'info');
        return;
      }
      if (task.state !== TASK_STATE.COMPLETED) {
        snackbar.show(task.error_message || 'Design import failed.', 'error', {
          autoHideDuration: OFFER_MS,
          action: viewTask,
        });
        return;
      }
      await offerProposal();
    } catch {
      consecutiveErrors += 1;
      if (consecutiveErrors < MAX_CONSECUTIVE_ERRORS) {
        window.setTimeout(poll, POLL_MS * 2);
      }
      // else: give up quietly — Design Imports → Proposals still shows it.
    }
  };

  window.setTimeout(poll, POLL_MS);
}
